import cn from 'classnames'
import Link from 'next/link'
import { useState } from 'react'
import { HtmlContentViewer } from 'src/components/global/HtmlContentViewer/HtmlContentViewer'
import VendorLogo from 'src/components/global/VendorLogo/VendorLogo'
import {
  IContentItemWriterFragment,
  IContentWriterFragment,
  IEnum_Componentcontentcontentwriter_Writeroption,
} from 'src/graphql/generated/hooks'
import { useContentWriterInformation } from 'src/hooks/useContentWriterInformation'
import { getFullName } from 'src/utils/getFullName'
import { removeTags } from 'src/utils/removeTags'

interface Props {
  writer: IContentWriterFragment | IContentItemWriterFragment | string
  className?: string
}

export const WriterSection: React.FC<Props> = ({ className, writer }) => {
  const [showMore, setShowMore] = useState<boolean>(true)
  const writerInfo = useContentWriterInformation(writer)
  if (!writerInfo) {
    return <></>
  }

  const title = removeTags(writerInfo?.title || '')
  const name = removeTags(writerInfo?.name || '')
  const company = removeTags(writerInfo?.company || '')
  const description = writerInfo?.description
  const subLabel = getFullName([title, company])

  return (
    <div className={cn('border-1 border-solid border rounded-[.3rem] mb-[3rem] md:mb-[5rem] border-mist', className)}>
      <div className="print-flex  w-full bg-clouds flex justify-between items-center h-[60px] px-[30px]">
        <span className="font-secondary font-bold text-[17px] leading-[26px] text-darkgray">
          About the{' '}
          {writerInfo.option === IEnum_Componentcontentcontentwriter_Writeroption.Expert ? 'Expert' : 'Author'}
        </span>
        {writerInfo.url && (
          <Link
            href={writerInfo.url}
            className="font-secondary font-bold text-[14px] text-textdark leading-[40px] underline underline-offset-[2px]"
          >
            View Profile
          </Link>
        )}
      </div>
      <div className="print-flex about-auther w-full px-8 pt-6 pb-6 flex flex-col md:flex-row items-center md:items-start">
        <div
          className={cn(
            'min-w-[100px] min-h-[100px] max-w-[100px] max-h-[100px] flex items-center justify-center overflow-hidden object-cover md:mr-[30px]',
            className,
          )}
        >
          <VendorLogo alt={name} src={writerInfo?.logo} />
        </div>
        <div className="pt-2 about-auther-text">
          {name && (
            <p className="font-secondary font-bold leading-[30px] text-textdark text-[20px] mb-[.1rem]">{name}</p>
          )}
          {subLabel && (
            <p className="font-secondary font-regular leading-[25px] text-darkgray text-[16px] mb-[15px] tracking-tighter">
              {subLabel}
            </p>
          )}
          <HtmlContentViewer
            html={description || ''}
            className={cn(
              'header-des-html text-[0.9375rem] font-primary font-regular text-darkgray',
              { 'line-clamp-4': showMore },
              { 'mt-[1.2rem]': !title },
            )}
          />
          {description && (
            <div
              className="w-full text-[0.875rem] font-primary font-regular text-textdark leading-[1.125rem] mb-[1rem] cursor-pointer mt-1"
              onClick={() => setShowMore(!showMore)}
            >
              {showMore ? 'View more' : 'View less'}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
